import React, { useState } from 'react';
import {
  initialPublicAuthorityFilterValues,
  PublicAuthorityOverviewFilterModel,
} from 'app/edozvola/public-authority-overview/model/public-authority-overview.model';
import { Col, Collapse, FormGroup, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Button, Label } from 'edozvola-frontend-lib/dist/ui';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { isEqual } from 'lodash';
import { useFormik } from 'formik';
import { paginationKeys } from 'app/shared/util/pagination.constants';
import {fetchCityMunicipalitiesWithCountyQuery, fetchCountiesQuery } from 'app/edozvola/codebooks/codebooks.queries';
import { Input } from 'edozvola-frontend-lib/dist/ui/components';
import {
  publicAuthorityOverviewFilterSchema
} from 'app/edozvola/public-authority-overview/model/public-authority-overview.schema';

export interface PublicAuthorityFilterProps {
  filterForm: PublicAuthorityOverviewFilterModel;
  setFilterForm: any;
  modal: any;
  closeModal: any;
}

export const PublicAuthorityFilterModal = (props: PublicAuthorityFilterProps) => {
  const [publicAuthoritySectionShown, setPublicAuthoritySectionShown] = useState<boolean>(true);
  const [affiliationAreaSectionShown, setAffiliationAreaSectionShown] = useState<boolean>(true);

  const formik = useFormik({
    initialValues: props.filterForm,
    onSubmit: values => {
      props.setFilterForm(values);
      localStorage.setItem(`${paginationKeys.PUBLIC_AUTHORITY_OVERVIEW}-filter-form`, JSON.stringify(values));
      props.closeModal();
    },
    enableReinitialize: true,
    validationSchema: publicAuthorityOverviewFilterSchema,
    validateOnChange: true,
    validateOnMount: true
  });

  const closeModal = () => {
    formik.setValues(props.filterForm);
    props.closeModal();
  };

  const clearFilter = () => {
    formik.resetForm({ values: initialPublicAuthorityFilterValues });
  };

  const { data: countiesOptions } = fetchCountiesQuery();
  const { data: cityMunicipalityOptions } = fetchCityMunicipalitiesWithCountyQuery();

  return (
    <Modal scrollable size={'xl'} backdrop="static" isOpen={!!props.modal} toggle={() => closeModal()} centered={true}>
      <ModalHeader close={<FontAwesomeIcon className={'hand'} icon={faXmark} />} onClick={e => closeModal()} className={'d-flex'}>
        Pretraga javnopravnih tijela
      </ModalHeader>
      <ModalBody className={'modal-body-dropdown-on-top'}>
        <Row className={'hand'} onClick={() => setPublicAuthoritySectionShown(prevState => !prevState)}>
          <Col xs={6}>
            <div className={'form-header'}>Javnopravno tijelo</div>
          </Col>
          <Col xs={6} className={'d-flex justify-content-end'}>
            <FontAwesomeIcon size="lg" color="outline" icon={publicAuthoritySectionShown ? 'chevron-circle-up' : 'chevron-circle-down'} />
          </Col>
        </Row>
        <Collapse isOpen={publicAuthoritySectionShown}>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Label labelText={'OIB'} />
                <Input
                  id={'oib'}
                  name={'oib'}
                  maxLength={11}
                  value={formik.values.oib}
                  disabled={formik.values.fullName?.length > 0}
                  onChange={e => formik.setFieldValue('oib', e.target.value)}
                  error={formik.errors?.oib ?? ''}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Label labelText={'Naziv'} />
                <Input
                  id={'fullName'}
                  name={'fullName'}
                  value={formik.values.fullName}
                  disabled={formik.values.oib?.length > 0}
                  onChange={e => formik.setFieldValue('fullName', e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>
        <Row className={'hand'} onClick={() => setAffiliationAreaSectionShown(prevState => !prevState)}>
          <Col xs={6}>
            <div className={'form-header'}>Područje nadležnosti</div>
          </Col>
          <Col xs={6} className={'d-flex justify-content-end'}>
            <FontAwesomeIcon size="lg" color="outline" icon={affiliationAreaSectionShown ? 'chevron-circle-up' : 'chevron-circle-down'} />
          </Col>
        </Row>
        <Collapse isOpen={affiliationAreaSectionShown}>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Label labelText={'Županija'} />
                <PaginatedDropdown
                  onItemSelect={option => {
                    formik.setFieldValue('cityMunicipality', []);
                    formik.setFieldValue('county', option);
                  }}
                  selected={formik.values.county}
                  labelKey={'value'}
                  options={countiesOptions ?? []}
                  disabled={formik.values?.cityMunicipality.length > 0}
                  positionFixed
                  clearButton
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Label labelText={'Grad/općina'} />
                <PaginatedDropdown
                  onItemSelect={option => {
                    formik.setFieldValue('county', []);
                    formik.setFieldValue('cityMunicipality', option);
                  }}
                  selected={formik.values.cityMunicipality}
                  labelKey={'value'}
                  options={cityMunicipalityOptions ?? []}
                  disabled={formik.values?.county.length > 0}
                  positionFixed
                  clearButton
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>
        <ModalFooter>
          <div>
            <Button
              color={'secondary'}
              size={'lg'}
              label={'Poništi'}
              prependIcon={'repeat'}
              onClick={clearFilter}
              disabled={isEqual(formik.values, initialPublicAuthorityFilterValues)}
            />
          </div>
          <div className={'float-end'}>
            <Button
              color={'info'}
              size={'lg'}
              label={'Pretraži'}
              prependIcon={'search'}
              onClick={formik.submitForm}
              disabled={!formik.isValid || isEqual(formik.values, props.filterForm)}
            />
          </div>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
}

